import React, { useEffect, useMemo, useState, useRef } from "react";
import styles from "./project.module.scss";
import ListingImages from "@/components/Listing/ListingImages";
import { usePath } from "@/contexts/path/path";
import { CloudfrontImageLoader, financialFormat } from "@/lib/helpers";
import Image from "next/image";
import { Button, Modal, Spinner } from "@aqar-app/react-ui";
import ProjectVideo from "@/components/ProjectVideosPreviews/projectVideo";
import dynamic from "next/dynamic";
import ProjectUnits from "@/containers/project-screen/ProjectUnits";
import { getIamModalProps } from "@/components/iam/isBlocked";
import RedBreadcrumb from "@/components/RedBreadCrumb/RedBreadCrumb";
import { getProjectURL } from "./ProjectCard";

import { STATUS_MAP, TYPE_MAP } from "@/common/constants";
import useFindProject from "@/containers/project-screen/useFindProject";
import Icon from "@/components/icon";
import { useLazyQuery, useMutation } from "@apollo/client";
import {
  GET_RED_PROJECT_PHONE,
  GET_RED_PROJECT_WHATSAPP,
  SHOW_INTEREST_MUTATION,
} from "@/graphql/queries/realestateProjects";
import { useAuthState } from "@/contexts/auth/auth.provider";
import { ErrorModal } from "@/components/error/ssr";
import { SuccessAlert } from "@/components/login-alert";
import { useRouter } from "next/router";
import SEO from "@/components/seo";
import config from "@/common/config";
import {
  INCREMENT_PROJECT_STATS,
  INCREMENT_PROJECT_VIEWS,
} from "@/graphql/mutations/realEstateProjects";
import ExternalLink from "../../components/external-link";
import {
  drawCircle,
  drawMarkerForSimpleListing,
} from "@/components/Listing/ListingScreen/DrawMarkerForSimpleListing";
import {
  trackRegisterInterest,
  trackCallDeveloper,
  trackContactUserProject,
  trackGetProjectLocation,
  trackPlayProjectVideo,
} from "@/lib/tracking/trackRegisterInterest";
import { DesiredServicesMap } from "@/hooks/useIam";
import AppLink from "@/components/new-resolver/app.ink";
import CollapsableText from "@/containers/project-screen/CollapsableText";
import {
  INIT_WHATSAPP_PROJECT_BROKER,
  INIT_WHATSAPP_PROJECT_LOCATION,
} from "@/graphql/mutations/user";
import { isSafari } from "@/lib/browserHelpers";
import useRequestProjectLocation from "@/hooks/useRequestProjectLocation";

const LeafletMap = dynamic(() =>
  import("../../components/Listing/ListingScreen/LeafletMap").then(
    (m) => m.LeafletMap,
  ),
);

const UiModal = dynamic(() => import("@/components/modal/Modal"), {
  ssr: false,
});

function RegisterInterestButton({ project }) {
  const { token, user } = useAuthState();
  const { previousPath } = usePath();
  const router = useRouter();
  const [modalProps, setModalProps] = useState({ visible: false });
  const [registerInterest, { loading }] = useMutation(SHOW_INTEREST_MUTATION, {
    context: { token },
    variables: {
      project_id: project.id,
    },
  });

  return (
    <>
      <UiModal {...modalProps} />
      <Button
        className={styles.interest}
        theme="info"
        outline
        onClick={(e) => {
          if (!token) return router.push("/login?returnTo=" + router.asPath);
          if (project.external_url) {
            trackRegisterInterest({
              id: project.id,
              source: previousPath || encodeURI(document.referrer),
              journey: "project page",
            });
            window.open(project.external_url, "_blank");
          }
          if (!user.iam_verified && project?.require_iam_to_book)
            return setModalProps({
              ...getIamModalProps(),
              desiredService: DesiredServicesMap.register_interest,
              onCancel: () => {
                setModalProps({ open: false });
              },
            });
          registerInterest()
            .then(() => {
              trackRegisterInterest({
                id: project.id,
                source: previousPath || encodeURI(document.referrer),
                journey: "project page",
              });
              Modal.open(<SuccessAlert text="تم تسجيل اهتماك بنجاح" />)(e);
            })
            .catch(ErrorModal.handle);
        }}
        disabled={loading}
      >
        {loading ? <Spinner /> : null}
        <span className={styles.icon} />
        <span className={styles.text}>تسجيل إهتمام</span>
      </Button>
    </>
  );
}
export function ShowPhoneButton({
  project,
  btnStyle,
  compact,
  iconName,
  iconComponent,
  onCall,
  onShowPhone,
}) {
  const [getPhone, { loading, data }] = useLazyQuery(GET_RED_PROJECT_PHONE, {
    // context: { token },
    variables: {
      project_id: project.id,
    },
  });

  const renderIcon = () => {
    if (iconComponent) {
      return iconComponent;
    }
    return (
      <Icon
        className={styles.icon}
        name={iconName || "Phone-white"}
        width={14}
        height={14}
      />
    );
  };

  return (
    <ExternalLink link={project.external_url}>
      <Button
        className={styles.phone}
        theme="default"
        outline
        onClick={(e) => {
          const phone = data?.RealEstateProjects?.getPhone;
          if (phone && !project.external_url) {
            onCall?.();
            return open("tel:" + phone);
          }
          getPhone().catch(ErrorModal.handle);
          onShowPhone?.();
        }}
        disabled={loading}
        style={btnStyle}
      >
        {loading ? <Spinner /> : null}
        {!data?.RealEstateProjects?.getPhone && renderIcon()}
        <span
          className={styles.text}
          dir={data?.RealEstateProjects?.getPhone ? "ltr" : undefined}
        >
          {project.external_url
            ? "إظهار رقم الجوال"
            : data?.RealEstateProjects?.getPhone || (
                <>
                  {data?.RealEstateProjects?.getPhone === null
                    ? "لا يوجد رقم"
                    : `${compact ? "اتصال" : "إظهار رقم الجوال"}`}
                </>
              )}
        </span>
      </Button>
    </ExternalLink>
  );
}

export function WhatsAppButton({
  project,
  btnStyle,
  compact,
  iconComponent,
  onClick,
  unitId,
}) {
  const { token } = useAuthState();

  const [initWhatsapp, { loading }] = useMutation(
    INIT_WHATSAPP_PROJECT_BROKER,
    {
      context: { token },
    },
  );

  return (
    <Button
      className={styles.whatsapp}
      theme="primary"
      onClick={async () => {
        onClick?.();
        if (project.external_url) {
          // return window.open(project.external_url, "_blank");
          if (isSafari()) {
            window.location.href = project.external_url;
          } else {
            window.open(project.external_url, "_blank");
          }
          return;
        }

        const { data } = await initWhatsapp({
          variables: {
            project_id: project.id,
            language: "ar",
            listing_id: unitId,
          },
        });
        if (data?.User?.initWhatsappUserProjectBrokerCommunication) {
          const { redirect_url } =
            data.User.initWhatsappUserProjectBrokerCommunication;
          // window.open(redirect_url, "_blank");
          if (isSafari()) {
            window.location.href = redirect_url;
          } else {
            window.open(redirect_url, "_blank");
          }
        } else {
          ErrorModal.handle({
            message: "فشل في فتح الواتساب",
          });
        }
      }}
      disabled={loading}
      style={btnStyle}
    >
      {loading ? <Spinner /> : "تواصل"}
    </Button>
  );
}

export const useProjectStats = (projectId) => {
  const [incrementStats] = useMutation(INCREMENT_PROJECT_STATS);
  const sessionTimeout = useRef(null);

  const trackStat = async (statsType) => {
    if (!projectId) return;

    try {
      await incrementStats({
        variables: {
          project_id: projectId,
          stats_type: statsType,
        },
      });
    } catch (error) {
      console.error("Failed to track project stat:", error);
    }
  };

  useEffect(() => {
    if (!projectId) return;

    trackStat("views");
    let seconds = 30;
    const intervalId = setInterval(() => {
      trackStat(`session_${seconds}_seconds`);
      seconds += 30;
      if (seconds > 120) clearInterval(intervalId);
    }, 30000);

    return () => {
      clearInterval(intervalId);
    };
  }, [projectId]);

  return {
    trackPhoneView: () => trackStat("phone_views"),
    trackCall: () => trackStat("calls"),
    trackWhatsapp: () => trackStat("whatsapp"),
  };
};

const ProjectView = () => {
  const { loadingProject, updateContext } = usePath();
  const { categoriesDictionary, previousPath } = usePath();
  const [project, listings] = useFindProject();
  const { trackPhoneView, trackCall, trackWhatsapp } = useProjectStats(
    project?.id,
  );
  const [collapsedFeatures, setCollapsedFeatures] = useState(true);

  const [incrementView] = useMutation(INCREMENT_PROJECT_VIEWS);

  const { handleRequestLocation } = useRequestProjectLocation(project?.id);

  useEffect(() => {
    if (project?.id) {
      incrementView({ variables: { project_id: project.id } });
    }
  }, [project?.id]);

  const getCategoryName = (categoryId) => {
    return categoriesDictionary?.[categoryId]?.name || "";
  };

  const {
    project_status: projectStatus,
    project_type: projectType,
    listing_categories: listingCategories,
    real_estate_developer: realEstateDeveloper,
    area_min: areaMin,
    area_max: areaMax,
    price_from: priceFrom,
    price_to: priceTo,
    description,
    preview_video: previewVideos,
    location,
    nearby_landmarks: nearbyLandmarks,
  } = project;

  console.log("nearbyLandmarks", nearbyLandmarks);

  const categoryNames = listingCategories?.map(getCategoryName) || [];
  const previewVideo = previewVideos?.[0];

  const specs = useMemo(() => {
    const specs = {};
    specs.total = `${project?.totalUnits} وحدة`;
    let minCopy = "";
    let maxCopy = "";

    if (areaMin) {
      minCopy = `من ${financialFormat(areaMin)} م2 `;
    }
    if (areaMax) {
      maxCopy = `إلى ${financialFormat(areaMax)} م2`;
    }
    specs.area = `${minCopy} ${maxCopy}`;

    let fromCopy = "";
    let toCopy = "";

    if (priceFrom) {
      fromCopy = `من ${financialFormat(priceFrom)} `;
    }
    if (priceTo) {
      toCopy = `إلى ${financialFormat(priceTo)} `;
    }
    specs.price = `${fromCopy} ${toCopy} ${fromCopy || toCopy ? "ريال" : ""}`;
    return specs;
  }, [areaMin, areaMax, project?.totalUnits, priceFrom, priceTo]);

  const metaData = useMemo(() => {
    const features = project.meta_data?.features?.filter((m) => m.value) || [];
    const support_banks =
      project.meta_data?.support_banks?.filter((m) => m.value) || [];
    const facilities =
      project.meta_data?.facilities?.filter((m) => m.value) || [];
    const warranty_and_maintenance =
      project.meta_data?.warranty_and_maintenance?.filter((m) => m.value?.ar) ||
      [];
    const after_sale_services =
      project.meta_data?.after_sale_services?.filter((m) => m.value) || [];

    return {
      features,
      support_banks,
      facilities,
      warranty_and_maintenance,
      after_sale_services,
    };
  }, [project]);

  const featuresLimit = useMemo(
    () => (collapsedFeatures ? 6 : metaData?.features?.length),
    [collapsedFeatures, metaData.features?.length],
  );

  const parseIconName = (iconUrl) => {
    const parts = iconUrl.split("/");
    const lastPart = parts[parts.length - 1];
    const nameAndExtension = lastPart.split(".");
    return nameAndExtension[0];
  };

  // TODO: handle loading state
  if (loadingProject) {
    return null;
  }

  return (
    <div className={styles.projectScreen}>
      <SEO
        title={`المشاريع العقارية | ${realEstateDeveloper?.name} | ${project.title} | تطبيق عقار`}
        description={project?.description}
        image={
          project?.imgs?.[0]
            ? `https://images.aqar.fm/webp/350x0/props/${project?.imgs?.[0]}`
            : null
        }
      />
      <div className={styles.projectContainer}>
        <RedBreadcrumb />
        <div className={styles.galleryWrapper}>
          <ListingImages
            listing={{
              imgs: project.imgs,
              title: project.title,
              id: project.id,
            }}
          />
          <div className={styles.overlay}>
            <div className={styles.tags}>
              {projectStatus && (
                <div className={`${styles.metaChip} ${styles[projectStatus]}`}>
                  <span>{STATUS_MAP[projectStatus]}</span>
                </div>
              )}
              {projectType && (
                <div className={styles.metaChip}>
                  <span>{TYPE_MAP[projectType]}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className={`${styles.developerBox} ${styles.withBorderBottom}`}>
          <div className={styles.right}>
            {!!categoryNames.length && (
              <div className={styles.categoriesList}>
                {categoryNames.map((categoryName) => (
                  <div className={styles.metaChip} key={categoryName}>
                    <span>{categoryName}</span>
                  </div>
                ))}
                <>
                  {projectStatus && (
                    <div
                      className={`${styles.metaChip} ${styles[projectStatus]} ${styles.mobile}`}
                    >
                      <span>{STATUS_MAP[projectStatus]}</span>
                    </div>
                  )}
                  {projectType && (
                    <div className={`${styles.metaChip} ${styles.mobile}`}>
                      <span>{TYPE_MAP[projectType]}</span>
                    </div>
                  )}
                </>
              </div>
            )}
            <div className={styles.developerProfile}>
              <div
                onClick={() => {
                  updateContext({
                    project: null,
                    developer: {
                      ...realEstateDeveloper,
                      url: getProjectURL({
                        real_estate_developer: realEstateDeveloper,
                      }),
                    },
                  });
                }}
                className={styles.avatar}
              >
                <Image
                  loader={CloudfrontImageLoader}
                  src={`https://cdn.aqar.fm/projects/${realEstateDeveloper?.logo_img}`}
                  fill
                  alt="logo"
                  sizes="(max-width: 600px) 40vw, 100vw"
                />
              </div>
              <div className={styles.developerInfo}>
                <AppLink
                  onClick={() => {
                    updateContext({
                      project: null,
                      developer: {
                        ...realEstateDeveloper,
                        url: getProjectURL({
                          real_estate_developer: realEstateDeveloper,
                        }),
                      },
                    });
                  }}
                  path={getProjectURL({
                    real_estate_developer: realEstateDeveloper,
                  })}
                  className={styles.name}
                >
                  {project.title} | {realEstateDeveloper?.name}
                </AppLink>
                <div className={styles.about}>{project?.address}</div>
              </div>
            </div>
          </div>
          <div className={styles.btns}>
            <WhatsAppButton
              project={project}
              onClick={() => {
                trackWhatsapp();
                trackContactUserProject({
                  source: previousPath || encodeURI(document.referrer),
                });
              }}
              btnStyle={{ minWidth: "130px" }}
            />
          </div>
        </div>

        <div className={`${styles.generalInfo} ${styles.withBorderBottom}`}>
          <div className={styles.element}>
            <Icon name="red-units" width={40} height={40} />
            <div className={styles.text}>
              <h2 className={styles.title}>الوحدات</h2>
              <h3 className={styles.value}>{specs.total}</h3>
            </div>
          </div>
          <div className={styles.element}>
            <Icon name="red-area-range" width={40} height={40} />
            <div className={styles.text}>
              <h2 className={styles.title}>المساحات</h2>
              <h3 className={styles.value}>{specs.area}</h3>
            </div>
          </div>
          <div className={styles.element}>
            <Icon name="red-price-range" width={40} height={40} />
            <div className={styles.text}>
              <h2 className={styles.title}>الأسعار</h2>
              <h3 className={styles.value}>{specs.price}</h3>
            </div>
          </div>
        </div>

        {description && (
          <div className={`${styles.description} ${styles.withBorderBottom}`}>
            <CollapsableText moreClassName={styles.showMore} numberOfLines={2}>
              {description}
            </CollapsableText>
          </div>
        )}

        {!!metaData?.features?.length && (
          <div className={`${styles.features} ${styles.withBorderBottom}`}>
            <div className={styles.titleWrapper}>
              <h2 className={styles.title}>مميزات المشروع</h2>
              {!(!collapsedFeatures || metaData?.features.length <= 6) && (
                <span
                  onClick={() => setCollapsedFeatures(false)}
                  className={styles.showMore}
                >
                  عرض الكل
                </span>
              )}
            </div>
            <div className={styles.featuresList}>
              {metaData.features.slice(0, featuresLimit).map((feature) => (
                <div className={styles.feature} key={feature.meta_key_id}>
                  <Image
                    alt={feature.meta_key_name?.ar}
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                    src={feature.icon_url_svg}
                    width={40}
                    height={40}
                  />
                  <span className={styles.name}>
                    {feature.meta_key_name?.ar}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {previewVideo?.link && (
          <div className={`${styles.section}`}>
            <h2 className={styles.sectionTitle}>تعرف أكثر</h2>
            <div>
              <ProjectVideo
                videoItem={previewVideo}
                onTogglePlay={trackPlayProjectVideo}
              />
            </div>
          </div>
        )}

        {!!metaData?.warranty_and_maintenance?.length && (
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>الضمان والصيانة</h2>
            <div className={styles.warrantyIconsList}>
              {metaData.warranty_and_maintenance.map((meta) => (
                <div className={styles.feature} key={meta.meta_key_id}>
                  <Image
                    src={meta.icon_url_svg}
                    width={40}
                    height={40}
                    alt={meta.meta_key_name?.ar}
                  />
                  <div>
                    <span className={styles.name}>
                      {meta.meta_key_name?.ar}
                    </span>
                    <p className={styles.value}>{meta.value?.ar}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>الموقع والمعالم القريبة</h2>
          <LeafletMap
            className={styles.mapWrapper}
            onMapReady={drawCircle({ location }, () => {
              trackGetProjectLocation({
                source: previousPath || encodeURI(document.referrer),
                page: "project_page",
              });
              return handleRequestLocation();
            })}
          >
            {!!nearbyLandmarks?.length && (
              <div id="landmarks" className={styles.nearbyLandmarks}>
                {nearbyLandmarks.map((landmark) => (
                  <div className={styles.landmark} key={landmark.landmark_id}>
                    <div className={styles.name}>
                      <Icon name="Pin" width={12} height={14} />
                      {landmark.name?.ar}
                    </div>
                    <div
                      className={styles.distance}
                    >{`${landmark.distance_in_km} كم`}</div>
                  </div>
                ))}
              </div>
            )}
          </LeafletMap>
          {/* <div className={styles.hideOnDesktop}>
            {!!nearbyLandmarks?.length && (
              <div className={styles.nearbyLandmarks}>
                {nearbyLandmarks.map((landmark) => (
                  <div className={styles.landmark} key={landmark.landmark_id}>
                    <div className={styles.name}>
                      <Icon name="Pin" width={12} height={14} />
                      {landmark.name?.ar}
                    </div>
                    <div
                      className={styles.distance}
                    >{`${landmark.distance_in_km} كم`}</div>
                  </div>
                ))}
              </div>
            )}
          </div> */}
        </div>

        {!!metaData?.after_sale_services?.length && (
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>خدمات ما بعد البيع</h2>
            <div className={styles.warrantyIconsList}>
              {metaData.after_sale_services.map((meta) => (
                <div className={styles.feature} key={meta.meta_key_id}>
                  <Image
                    className={styles.icon}
                    src={meta.icon_url_svg}
                    width={40}
                    alt={meta.meta_key_name?.ar}
                    height={40}
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                  <span className={styles.name}>{meta.meta_key_name?.ar}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <ProjectUnits listings={listings} project={project} />
      </div>
    </div>
  );
};

export default ProjectView;
